import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Users,
  FileText,
  Phone,
  ExternalLink,
  Calendar
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import { clearAuth, getToken, getUser } from '@/utils/auth';
import { API_BASE_URL } from '@/utils/env';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CountryFilter from '../components/CountryFilter';
import AdminProfileMenu from '../components/AdminProfileMenu';

type RaisingForm = {
  raiseAmount?: string;
  roundType?: string;
  capitalType?: string;
  targetValuation?: string;
  companyInfo?: string;
  website?: string;
  completedAt?: string;
};

type UserRecord = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  phoneNumber?: string;
  country?: string;
  createdAt?: string;
  onboardingCompleted?: boolean;
  irScore?: {
    totalScore?: number;
    status?: string;
    category?: string;
  } | null;
  raiseCapitalForm?: RaisingForm | null;
  accountNumber?: string;
};

type Filters = {
  country: string;
  startDate: string;
  endDate: string;
};

// Mockup date format: "15 Jul 2026".
const formatDate = (value?: string) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return value;
  }
};

const avatarInitials = (firstName?: string, lastName?: string) => {
  const initials = [firstName?.[0], lastName?.[0]].filter(Boolean).join('');
  return initials || 'C';
};

// Deterministic avatar swatches — mirror the mockup's varied client colours.
const AVATAR_COLORS = [
  { bg: '#DBEAFE', color: '#1D4ED8' },
  { bg: '#FEF6E7', color: '#B45309' },
  { bg: '#E9F7EF', color: '#15803D' },
  { bg: '#EEF2F6', color: '#475569' },
  { bg: '#FDF2F8', color: '#BE185D' }
];
const avatarColor = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
};

// Visual-only helper: map an IR-score to a coloured band (score first, category fallback).
type IrVariant = 'green' | 'brand' | 'amber' | 'red' | 'muted';
const irScoreVariant = (irScore?: UserRecord['irScore']): IrVariant => {
  if (!irScore) return 'muted';
  if (typeof irScore.totalScore === 'number') {
    const s = irScore.totalScore;
    if (s >= 75) return 'green';
    if (s >= 55) return 'brand';
    if (s >= 40) return 'amber';
    return 'red';
  }
  const cat = (irScore.category || irScore.status || '').toLowerCase();
  if (cat.includes('investor') || cat.includes('exceptional')) return 'green';
  if (cat.includes('semi')) return 'brand';
  if (cat.includes('medium') || cat.includes('early')) return 'amber';
  if (cat.includes('risk')) return 'red';
  return 'brand';
};

// Visual-only helper: product pill colour (Equity = brand, Convertible/SAFE = amber).
const productVariant = (capitalType?: string): 'brand' | 'amber' => {
  const c = (capitalType || '').toLowerCase();
  if (c.includes('safe') || c.includes('convertible')) return 'amber';
  return 'brand';
};

export default function RaisingRequest() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [filters, setFilters] = useState<Filters>({ country: '', startDate: '', endDate: '' });
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);
  const [dateSortDir, setDateSortDir] = useState<'asc' | 'desc'>('desc');

  // Applied filters state
  const [appliedFilters, setAppliedFilters] = useState<Filters>({ country: '', startDate: '', endDate: '' });
  const [appliedQuickFilters, setAppliedQuickFilters] = useState<string[]>([]);

  const [adminName, setAdminName] = useState('Admin');
  const [adminEmail, setAdminEmail] = useState('');

  useEffect(() => {
    // Check for admin user first
    const adminUserStr = localStorage.getItem('adminUser');
    if (adminUserStr) {
      try {
        const adminUser = JSON.parse(adminUserStr);
        if (adminUser.email) {
          setAdminEmail(adminUser.email);
          setAdminName(adminUser.email);
          return;
        }
      } catch (err) {
        console.error('Failed to parse adminUser', err);
      }
    }

    // Fallback to regular user
    const currentUser = getUser();
    if (currentUser?.email) {
      setAdminEmail(currentUser.email);
      setAdminName(currentUser.email);
    } else if (currentUser?.displayName) {
      setAdminName(currentUser.displayName);
    } else if (currentUser?.firstName) {
      setAdminName(`${currentUser.firstName} ${currentUser.lastName ?? ''}`.trim());
    }
  }, []);

  useEffect(() => {
    fetchRaisingRequests();
  }, []);

  const fetchRaisingRequests = async () => {
    const token = getToken();
    setLoading(true);

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}/auth/users/all`, { headers });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch users:', response.status, errorText);
        return;
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        setUsers(data as UserRecord[]);
      }
    } catch (err) {
      console.error('Error fetching raising requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const raisingUsers = useMemo(
    () => users.filter((user) => user.raiseCapitalForm && Object.keys(user.raiseCapitalForm).length > 0),
    [users]
  );

  useEffect(() => {
    if (raisingUsers.length > 0) {
      console.log('🔍 Raising Users Data:', raisingUsers);
    }
  }, [raisingUsers]);

  const filteredUsers = useMemo(() => {
    let result = [...raisingUsers];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((user) =>
        user.email.toLowerCase().includes(query) ||
        `${user.firstName ?? ''} ${user.lastName ?? ''}`.toLowerCase().includes(query) ||
        user.phone?.includes(searchQuery) ||
        user.phoneNumber?.includes(searchQuery) ||
        user._id?.toLowerCase().includes(query) ||
        user.accountNumber?.toLowerCase().includes(query)
      );
    }

    if (appliedFilters.country) {
      const countryQuery = appliedFilters.country.toLowerCase();
      result = result.filter((user) => user.country?.toLowerCase().includes(countryQuery));
    }

    if (appliedFilters.startDate) {
      const start = new Date(appliedFilters.startDate).getTime();
      result = result.filter((user) => {
        const completedAt = user.raiseCapitalForm?.completedAt || user.createdAt;
        return completedAt ? new Date(completedAt).getTime() >= start : false;
      });
    }

    if (appliedFilters.endDate) {
      const end = new Date(appliedFilters.endDate).getTime();
      result = result.filter((user) => {
        const completedAt = user.raiseCapitalForm?.completedAt || user.createdAt;
        return completedAt ? new Date(completedAt).getTime() <= end : false;
      });
    }

    if (appliedQuickFilters.includes('new')) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      result = result.filter((user) => {
        const completedAt = user.raiseCapitalForm?.completedAt || user.createdAt;
        return completedAt ? new Date(completedAt) >= thirtyDaysAgo : false;
      });
    }

    return result.sort((a, b) => {
      const aDate = new Date(a.raiseCapitalForm?.completedAt || a.createdAt || 0).getTime();
      const bDate = new Date(b.raiseCapitalForm?.completedAt || b.createdAt || 0).getTime();
      return dateSortDir === 'asc' ? aDate - bDate : bDate - aDate;
    });
  }, [raisingUsers, searchQuery, appliedFilters, appliedQuickFilters, dateSortDir]);

  const totalRequests = raisingUsers.length;
  const newRequests = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return raisingUsers.filter((user) => {
      const completedAt = user.raiseCapitalForm?.completedAt || user.createdAt;
      return completedAt ? new Date(completedAt) >= thirtyDaysAgo : false;
    }).length;
  }, [raisingUsers]);

  const toggleQuickFilter = (filterKey: string) => {
    setActiveQuickFilters(prev =>
      prev.includes(filterKey)
        ? prev.filter(f => f !== filterKey)
        : [...prev, filterKey]
    );
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilters({ country: '', startDate: '', endDate: '' });
    setActiveQuickFilters([]);
    setAppliedFilters({ country: '', startDate: '', endDate: '' });
    setAppliedQuickFilters([]);
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/admin-login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8]">
        <div className="text-slate-500 text-sm">{t('admin.raising.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <AdminSidebar />

      {/* Main Content */}
      <main className="flex-1 xl:ms-64 adminx">
        <style>{`
          .adminx { font-family: 'Poppins', system-ui, sans-serif; color: #0F172A; }
          .adminx *, .adminx *::before, .adminx *::after { box-sizing: border-box; }

          /* Top bar */
          .adminx .rr-topbar { background:#fff; border-bottom:1px solid #E6E9ED; padding:14px 16px; display:flex; align-items:center; justify-content:space-between; gap:12px; position:sticky; top:0; z-index:10; box-shadow:0 1px 2px rgba(16,24,40,.04); }
          @media (min-width:768px){ .adminx .rr-topbar{ padding:16px 32px; } }
          .adminx .rr-search { position:relative; flex:1 1 auto; max-width:640px; }
          .adminx .rr-search svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); width:18px; height:18px; color:#94A3B8; pointer-events:none; }
          .adminx .rr-search input { width:100%; padding:10px 14px 10px 40px; background:#F8FAFC; border:1px solid #E6E9ED; border-radius:10px; font-size:14px; color:#0F172A; outline:none; transition:border-color .15s, box-shadow .15s, background .15s; }
          .adminx .rr-search input:focus { border-color:#2563EB; box-shadow:0 0 0 3px rgba(37,99,235,.12); background:#fff; }
          .adminx .rr-search input::placeholder { color:#94A3B8; }

          /* Page body */
          .adminx .rr-body { padding:16px; max-width:1460px; margin:0 auto; }
          @media (min-width:768px){ .adminx .rr-body{ padding:26px 32px 48px; } }

          /* Cards */
          .adminx .rr-card { background:#fff; border:1px solid #E6E9ED; border-radius:14px; box-shadow:0 1px 2px rgba(15,23,42,.05); }
          .adminx .rr-section { padding:20px; }
          @media (min-width:768px){ .adminx .rr-section{ padding:24px; } }

          /* Stat cards (mockup: icon on top, stacked) */
          .adminx .rr-stats { display:grid; grid-template-columns:1fr; gap:16px; }
          @media (min-width:640px){ .adminx .rr-stats{ grid-template-columns:repeat(2,1fr); } }
          .adminx .rr-stat { display:flex; flex-direction:column; padding:18px 20px; }
          .adminx .rr-stat-icon { width:34px; height:34px; border-radius:9px; background:#DBEAFE; color:#1D4ED8; display:flex; align-items:center; justify-content:center; margin-bottom:12px; }
          .adminx .rr-stat-icon svg { width:17px; height:17px; }
          .adminx .rr-stat-num { font-size:25px; font-weight:700; line-height:1.15; color:#0F172A; }
          .adminx .rr-stat-label { font-size:12px; color:#64748B; margin-top:2px; }
          .adminx .rr-stat-sub { font-size:11px; color:#94A3B8; margin-top:4px; }

          /* Headings */
          .adminx .rr-h3 { font-size:15px; font-weight:600; color:#0F172A; }
          .adminx .rr-muted { font-size:12px; color:#64748B; }
          .adminx .rr-label { font-size:11px; font-weight:600; letter-spacing:.6px; text-transform:uppercase; color:#64748B; }

          /* Filters */
          .adminx .rr-filters-grid { display:grid; grid-template-columns:1fr; gap:14px; }
          @media (min-width:768px){ .adminx .rr-filters-grid{ grid-template-columns:repeat(3,1fr); } }
          .adminx .rr-field { position:relative; }
          .adminx .rr-field .rr-cal { position:absolute; left:12px; top:50%; transform:translateY(-50%); width:16px; height:16px; color:#94A3B8; pointer-events:none; }
          .adminx .rr-input { width:100%; padding:10px 14px 10px 38px; border:1px solid #E6E9ED; border-radius:9px; font-size:14px; color:#0F172A; outline:none; background:#fff; transition:border-color .15s, box-shadow .15s; }
          .adminx .rr-input:focus { border-color:#2563EB; box-shadow:0 0 0 3px rgba(37,99,235,.12); }
          .adminx .rr-input::placeholder { color:#94A3B8; }

          /* Chips + buttons */
          .adminx .rr-chip { padding:7px 14px; border-radius:999px; font-size:12.5px; font-weight:500; background:#fff; color:#334155; border:1px solid #E6E9ED; cursor:pointer; transition:all .15s; }
          .adminx .rr-chip:hover { border-color:#D5DAE1; }
          .adminx .rr-chip--active { background:#0F172A; color:#fff; border-color:#0F172A; }
          .adminx .rr-btn-primary { display:inline-flex; align-items:center; justify-content:center; gap:7px; padding:9px 18px; font-size:13px; font-weight:600; color:#fff; background:#2563EB; border:1px solid #2563EB; border-radius:9px; cursor:pointer; transition:background .15s, border-color .15s; }
          .adminx .rr-btn-primary:hover { background:#1D4ED8; border-color:#1D4ED8; }
          .adminx .rr-btn-ghost { display:inline-flex; align-items:center; justify-content:center; gap:7px; padding:9px 15px; font-size:13px; font-weight:600; color:#334155; background:#fff; border:1px solid #E6E9ED; border-radius:9px; cursor:pointer; transition:background .15s, border-color .15s; }
          .adminx .rr-btn-ghost:hover { border-color:#D5DAE1; background:#FAFBFC; }
          .adminx .rr-reset { width:40px; padding:9px 0; font-size:16px; line-height:1; }

          /* Table */
          .adminx .rr-table-wrap { overflow-x:auto; }
          .adminx table.rr-table { min-width:100%; border-collapse:collapse; font-size:13px; }
          .adminx .rr-table thead th { text-transform:uppercase; font-size:11px; letter-spacing:.6px; font-weight:600; color:#64748B; background:#FBFCFD; padding:10px 14px; text-align:left; border-bottom:1px solid #EFF1F4; white-space:nowrap; }
          .adminx .rr-table tbody td { padding:12px 14px; border-bottom:1px solid #EFF1F4; color:#475569; vertical-align:middle; }
          .adminx .rr-table tbody tr:last-child td { border-bottom:none; }
          .adminx .rr-table tbody tr:hover { background:#FCFDFE; }
          .adminx .rr-sort { cursor:pointer; user-select:none; }
          .adminx .rr-sort:hover { color:#334155; }
          .adminx .rr-arrow { font-size:9px; margin-inline-start:4px; }

          /* Client cell */
          .adminx .rr-client { display:flex; align-items:center; gap:10px; }
          .adminx .rr-avatar { width:34px; height:34px; border-radius:999px; background:#DBEAFE; color:#1D4ED8; font-weight:600; font-size:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
          .adminx .rr-client-name { font-size:13px; font-weight:600; color:#0F172A; }
          .adminx .rr-client-sub { font-size:11.5px; color:#64748B; }

          /* Phone */
          .adminx .rr-phone { display:inline-flex; align-items:center; gap:8px; }
          .adminx .rr-phone svg { width:15px; height:15px; color:#94A3B8; flex-shrink:0; }

          /* Pills (shared anatomy: dot + soft bg) */
          .adminx .rr-pill { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:999px; font-size:11.5px; font-weight:600; white-space:nowrap; }
          .adminx .rr-pill i { width:6px; height:6px; border-radius:999px; display:inline-block; flex-shrink:0; }
          .adminx .rr-pill--green { background:#E9F7EF; color:#15803D; } .adminx .rr-pill--green i{ background:#15803D; }
          .adminx .rr-pill--brand { background:#DBEAFE; color:#1D4ED8; } .adminx .rr-pill--brand i{ background:#1D4ED8; }
          .adminx .rr-pill--amber { background:#FEF6E7; color:#B45309; } .adminx .rr-pill--amber i{ background:#B45309; }
          .adminx .rr-pill--red { background:#FEF2F2; color:#DC2626; } .adminx .rr-pill--red i{ background:#DC2626; }
          .adminx .rr-pill--muted { background:#EFF1F4; color:#64748B; } .adminx .rr-pill--muted i{ background:#94A3B8; }

          /* View button */
          .adminx .rr-view { display:inline-flex; align-items:center; gap:6px; padding:6px 11px; font-size:12.5px; font-weight:600; color:#334155; background:#fff; border:1px solid #E6E9ED; border-radius:8px; cursor:pointer; transition:background .15s, border-color .15s; }
          .adminx .rr-view:hover { border-color:#D5DAE1; background:#FAFBFC; }
          .adminx .rr-view svg { width:14px; height:14px; }

          /* Empty state */
          .adminx .rr-empty { padding:44px 24px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:6px; }
          .adminx .rr-empty-ico { width:44px; height:44px; border-radius:12px; background:#EEF2F6; color:#94A3B8; display:flex; align-items:center; justify-content:center; margin-bottom:6px; }
          .adminx .rr-empty-ico svg { width:20px; height:20px; }
          .adminx .rr-empty b { font-size:14px; font-weight:600; color:#0F172A; }

          /* Mobile cards */
          .adminx .rr-mcard { background:#fff; border:1px solid #E6E9ED; border-radius:14px; padding:16px; }
          .adminx .rr-mhead { display:flex; align-items:center; gap:12px; padding-bottom:12px; margin-bottom:8px; border-bottom:1px solid #EFF1F4; }
          .adminx .rr-mrow { display:flex; align-items:center; justify-content:space-between; gap:12px; font-size:13px; padding:6px 0; }
          .adminx .rr-mrow .k { color:#94A3B8; }
          .adminx .rr-mrow .v { color:#0F172A; font-weight:500; text-align:right; }
          .adminx .rr-view--block { width:100%; justify-content:center; margin-top:14px; }
        `}</style>

        {/* Top Bar */}
        <header className="rr-topbar">
          <div className="rr-search xl:ms-0 ms-10">
            <Search />
            <input
              type="text"
              placeholder={t('admin.raising.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            />
          </div>
          <div className="flex items-center gap-2 md:gap-3 md:ml-4">
            <AdminProfileMenu />
          </div>
        </header>

        <div className="rr-body space-y-6">
          {/* Mini stat cards */}
          <div className="rr-stats">
            <div className="rr-card rr-stat" style={{ alignItems: isRTL ? 'flex-end' : 'flex-start', textAlign: isRTL ? 'right' : 'left' }}>
              <div className="rr-stat-icon">
                <FileText />
              </div>
              <div className="rr-stat-num">{newRequests}</div>
              <div className="rr-stat-label">{t('admin.raising.newRequests')}</div>
              <div className="rr-stat-sub">{t('admin.raising.last30Days')}</div>
            </div>

            <div className="rr-card rr-stat" style={{ alignItems: isRTL ? 'flex-end' : 'flex-start', textAlign: isRTL ? 'right' : 'left' }}>
              <div className="rr-stat-icon">
                <Users />
              </div>
              <div className="rr-stat-num">{totalRequests}</div>
              <div className="rr-stat-label">{t('admin.raising.allClients')}</div>
              <div className="rr-stat-sub">{t('admin.raising.totalRequests')}</div>
            </div>
          </div>

          {/* Filters */}
          <section className="rr-card rr-section">
            <div className="rr-filters-grid">
              <div className="rr-field">
                <CountryFilter
                  value={filters.country}
                  onChange={(val) => setFilters((prev) => ({ ...prev, country: val }))}
                  placeholder={t('admin.raising.placeholder.allCountries')}
                  className="w-full"
                />
              </div>
              <div className="rr-field">
                <DatePicker
                  selected={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(date: Date | null) => setFilters((prev) => ({ ...prev, startDate: date?.toISOString().split('T')[0] ?? '' }))}
                  placeholderText={t('admin.raising.placeholder.startDate')}
                  dateFormat="MMM d, yyyy"
                  className="rr-input"
                  calendarClassName="custom-calendar"
                  showPopperArrow={false}
                  isClearable
                />
                <Calendar className="rr-cal" />
              </div>
              <div className="rr-field">
                <DatePicker
                  selected={filters.endDate ? new Date(filters.endDate) : null}
                  onChange={(date: Date | null) => setFilters((prev) => ({ ...prev, endDate: date?.toISOString().split('T')[0] ?? '' }))}
                  placeholderText={t('admin.raising.placeholder.endDate')}
                  dateFormat="MMM d, yyyy"
                  className="rr-input"
                  calendarClassName="custom-calendar"
                  showPopperArrow={false}
                  minDate={filters.startDate ? new Date(filters.startDate) : undefined}
                  isClearable
                />
                <Calendar className="rr-cal" />
              </div>
            </div>

            <div className="mt-6 flex flex-col md:flex-row justify-between items-end gap-4">
              <div className="w-full" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                <h4 className="rr-label mb-3">{t('admin.raising.fastFilters')}</h4>
                <div className="flex flex-wrap gap-2.5 items-center" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    onClick={() => toggleQuickFilter('new')}
                    className={`rr-chip ${activeQuickFilters.includes('new') ? 'rr-chip--active' : ''}`}
                  >
                    {t('admin.raising.newRequests')}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <button
                  onClick={() => {
                    setAppliedFilters(filters);
                    setAppliedQuickFilters(activeQuickFilters);
                  }}
                  className="rr-btn-primary flex-1 md:flex-none"
                >
                  {t('admin.raising.applyFilters')}
                </button>
                <button
                  onClick={resetFilters}
                  className="rr-btn-ghost rr-reset"
                  title={t('admin.raising.refresh')}
                  aria-label={t('admin.raising.refresh')}
                >
                  ↺
                </button>
              </div>
            </div>
          </section>

          {/* Table */}
          <section className="rr-card rr-section">
            <div className="flex items-center justify-between mb-6" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                <h3 className="rr-h3">{t('admin.raising.referralTable')}</h3>
                <p className="rr-muted mt-1">{t('admin.raising.showingRange', { count: filteredUsers.length, total: totalRequests })}</p>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="block md:hidden space-y-4">
              {filteredUsers.length === 0 ? (
                <div className="rr-empty">
                  <div className="rr-empty-ico"><Search /></div>
                  <b>{t('admin.raising.noRequests')}</b>
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const phone = user.phone || user.phoneNumber || '—';
                  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || t('admin.raising.unnamedFounder');
                  const completedDate = user.raiseCapitalForm?.completedAt || user.createdAt;
                  const productLabel = user.raiseCapitalForm?.capitalType
                    ? user.raiseCapitalForm.capitalType.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
                    : t('admin.raising.productLabelFallback');
                  const irVariant = irScoreVariant(user.irScore);
                  const irLabel = user.irScore?.totalScore != null
                    ? `${user.irScore.totalScore}/100`
                    : user.irScore
                      ? (user.irScore.category || t('admin.score.yes'))
                      : t('admin.score.no');
                  const prodVariant = productVariant(user.raiseCapitalForm?.capitalType);
                  const swatch = avatarColor(user._id || name);

                  return (
                    <div key={user._id} className="rr-mcard">
                      <div className="rr-mhead" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                        <div className="rr-avatar" style={{ background: swatch.bg, color: swatch.color }}>
                          {avatarInitials(user.firstName, user.lastName)}
                        </div>
                        <div className="flex-1" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                          <p className="rr-client-name">{name}</p>
                          <p className="rr-client-sub">{user.country || 'N/A'}</p>
                        </div>
                      </div>
                      <div>
                        <div className="rr-mrow">
                          <span className="k">{t('admin.table.email')}</span>
                          <span className="v" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</span>
                        </div>
                        <div className="rr-mrow">
                          <span className="k">{t('admin.table.irScore')}</span>
                          <span className={`rr-pill rr-pill--${irVariant}`}>
                            <i></i>
                            {irLabel}
                          </span>
                        </div>
                        <div className="rr-mrow">
                          <span className="k">{t('admin.table.phone')}</span>
                          <span className="v rr-phone">
                            <Phone />
                            {phone}
                          </span>
                        </div>
                        <div className="rr-mrow">
                          <span className="k">{t('admin.table.products')}</span>
                          <span className={`rr-pill rr-pill--${prodVariant}`}>
                            <i></i>
                            {productLabel}
                          </span>
                        </div>
                        <div className="rr-mrow">
                          <span className="k">{isRTL ? 'التاريخ' : 'Date'}</span>
                          <span className="v">{formatDate(completedDate)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/admin/user/${user._id}`)}
                        className="rr-view rr-view--block"
                      >
                        {t('admin.raising.view')}
                        <ExternalLink />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block rr-table-wrap">
              <table className="rr-table" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                <thead>
                  <tr>
                    <th>{t('admin.table.name')}</th>
                    <th>{t('admin.table.email')}</th>
                    <th>{t('admin.table.irScore')}</th>
                    <th>{t('admin.table.phone')}</th>
                    <th>{t('admin.table.products')}</th>
                    <th className="rr-sort" onClick={() => setDateSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
                      {isRTL ? 'التاريخ' : 'Date'}
                      <span className="rr-arrow">{dateSortDir === 'asc' ? '▲' : '▼'}</span>
                    </th>
                    <th style={{ textAlign: isRTL ? 'left' : 'right' }}>{t('admin.table.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <div className="rr-empty">
                          <div className="rr-empty-ico"><Search /></div>
                          <b>{t('admin.raising.noRequests')}</b>
                        </div>
                      </td>
                    </tr>
                  )}
                  {filteredUsers.map((user) => {
                    const phone = user.phone || user.phoneNumber || '—';
                    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || t('admin.raising.unnamedFounder');
                    const completedDate = user.raiseCapitalForm?.completedAt || user.createdAt;
                    const productLabel = user.raiseCapitalForm?.capitalType
                      ? user.raiseCapitalForm.capitalType.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
                      : t('admin.raising.productLabelFallback');
                    const irVariant = irScoreVariant(user.irScore);
                    const irLabel = user.irScore?.totalScore != null
                      ? `${user.irScore.totalScore}/100`
                      : user.irScore
                        ? (user.irScore.category || t('admin.score.yes'))
                        : t('admin.score.no');
                    const prodVariant = productVariant(user.raiseCapitalForm?.capitalType);
                    const swatch = avatarColor(user._id || name);

                    return (
                      <tr key={user._id}>
                        <td>
                          <div className="rr-client" title={`UID: ${user._id}`}>
                            <div className="rr-avatar" style={{ background: swatch.bg, color: swatch.color }}>
                              {avatarInitials(user.firstName, user.lastName)}
                            </div>
                            <div>
                              <p className="rr-client-name">{name}</p>
                              <p className="rr-client-sub">{user.country || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`rr-pill rr-pill--${irVariant}`}>
                            <i></i>
                            {irLabel}
                          </span>
                        </td>
                        <td>
                          <span className="rr-phone">
                            <Phone />
                            {phone}
                          </span>
                        </td>
                        <td>
                          <span className={`rr-pill rr-pill--${prodVariant}`}>
                            <i></i>
                            {productLabel}
                          </span>
                        </td>
                        <td>{formatDate(completedDate)}</td>
                        <td style={{ textAlign: isRTL ? 'left' : 'right' }}>
                          <button
                            onClick={() => navigate(`/admin/user/${user._id}`)}
                            className="rr-view"
                          >
                            {t('admin.raising.view')}
                            <ExternalLink />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
